import { useCallback, useEffect, useState } from 'react';
import { app, bridge, notes, type NoteAction, type NotesSnapshot } from './bridge';
import { describeError } from './errorMessage';

/**
 * 笔记操作的执行流程（详情页与列表批量共用）：原生确认框 → `notes.run` → 跟 `notes.progress` → 结束回调。
 *
 * 操作目录由原生 `notes.actions` 下发，这里不做任何平台判断。
 */
export function useNoteActions(accountId: string, onFinished: (snapshot: NotesSnapshot) => void) {
  const [snapshot, setSnapshot] = useState<NotesSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const running = snapshot?.state === 'running';

  useEffect(
    () =>
      bridge.on('notes.progress', (payload) => {
        const next = payload as NotesSnapshot & { accountId?: string };
        if (next.accountId && next.accountId !== accountId) return;
        setSnapshot(next);
        if (next.state === 'running') return;
        if (next.state === 'completed') {
          const failed = next.failed ?? 0;
          const unit = next.action === 'clearComments' ? '条' : '篇';
          void app.toast(failed ? `完成 ${next.done ?? 0} ${unit}，失败 ${failed} ${unit}` : '已完成', failed ? 'error' : 'success');
          void app.haptic(failed ? 'error' : 'success');
        } else if (next.state === 'failed') {
          void app.haptic('error');
        }
        onFinished(next);
      }),
    [accountId, onFinished],
  );

  /** 确认后执行；用户取消返回 false。 */
  const run = useCallback(
    async (action: NoteAction, ids: string[]) => {
      if (busy || running || !accountId || action.applicable === 0) return false;
      setError(null);
      const confirmed = await app.confirm({ ...action.confirm, destructive: action.destructive });
      if (!confirmed) return false;
      setBusy(true);
      try {
        setSnapshot(await notes.run(accountId, action.key, ids));
        return true;
      } catch (e) {
        setError(describeError(e));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [accountId, busy, running],
  );

  return { snapshot, running, busy, error, run };
}
